// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Loader2, ExternalLink, FileText, Pencil, Link as LinkIcon, ShieldCheck } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RichTextDisplay } from "@/design-system/forms/RichTextEditor";
import type { EventData, WaiverSignature, WaiverCatalogItem, EventWaiverSettings } from "@/types/events";

interface Props {
  event: EventData;
  updateEvent: (updates: Partial<EventData>) => void;
  waiverSignatures: WaiverSignature[];
  eventId?: string;
}

export function WaiversSection({ eventId }: Props) {
  const { organization } = useOrganization();
  const orgId = organization?.id;
  const [loading, setLoading] = useState(false);
  const [catalog, setCatalog] = useState<WaiverCatalogItem[]>([]);
  const [settings, setSettings] = useState<EventWaiverSettings>({
    event_id: eventId ?? "",
    team_waiver_id: null,
    individual_waiver_id: null,
    team_waiver_required: false,
    individual_waiver_required: false,
    collect_at: "registration",
    block_unsigned: true,
    team_waiver_version_pinned: null,
    individual_waiver_version_pinned: null,
  });
  const [previewing, setPreviewing] = useState<WaiverCatalogItem | null>(null);

  useEffect(() => {
    if (!eventId || !orgId) return;
    (async () => {
      setLoading(true);
      const [{ data: cat }, { data: cfg }] = await Promise.all([
        (supabase as any)
          .from("waiver_catalog")
          .select("*")
          .or(`organization_id.eq.${orgId},organization_id.is.null`)
          .eq("is_active", true),
        (supabase as any).from("event_waiver_settings").select("*").eq("event_id", eventId).maybeSingle(),
      ]);
      setCatalog((cat ?? []) as WaiverCatalogItem[]);
      if (cfg) setSettings(cfg as EventWaiverSettings);
      setLoading(false);
    })();
  }, [eventId, orgId]);

  const teamOptions = useMemo(
    () => catalog.filter(c => c.waiver_type === "team" || c.waiver_type === "both"),
    [catalog]
  );
  const individualOptions = useMemo(
    () => catalog.filter(c => c.waiver_type === "individual" || c.waiver_type === "both"),
    [catalog]
  );

  const persist = async (next: EventWaiverSettings) => {
    if (!eventId) return;
    setSettings(next);
    const payload = { ...next, event_id: eventId };
    const { error } = await (supabase as any)
      .from("event_waiver_settings")
      .upsert(payload, { onConflict: "event_id" });
    if (error) toast.error(error.message);
  };

  const selectTeamWaiver = (id: string | null) => {
    const w = catalog.find(c => c.id === id);
    persist({
      ...settings,
      team_waiver_id: id,
      team_waiver_version_pinned: w?.version ?? null,
    });
  };
  const selectIndividualWaiver = (id: string | null) => {
    const w = catalog.find(c => c.id === id);
    persist({
      ...settings,
      individual_waiver_id: id,
      individual_waiver_version_pinned: w?.version ?? null,
    });
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Pick which waivers teams and players must sign for this event.
          </p>
        </div>
        <a
          href="/admin-dashboard/waivers"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-primary inline-flex items-center gap-1 hover:underline"
        >
          Manage waiver catalog <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      {/* Team Waiver */}
      <WaiverSlot
        title="Team Waiver"
        toggleLabel="Require a team waiver for this event"
        required={settings.team_waiver_required}
        onToggleRequired={(v) => persist({ ...settings, team_waiver_required: v })}
        selectedId={settings.team_waiver_id}
        options={teamOptions}
        onSelect={selectTeamWaiver}
        onPreview={(w) => setPreviewing(w)}
        emptyType="team"
      />

      {/* Individual Waiver */}
      <WaiverSlot
        title="Individual Waiver"
        toggleLabel="Require an individual waiver for each player"
        required={settings.individual_waiver_required}
        onToggleRequired={(v) => persist({ ...settings, individual_waiver_required: v })}
        selectedId={settings.individual_waiver_id}
        options={individualOptions}
        onSelect={selectIndividualWaiver}
        onPreview={(w) => setPreviewing(w)}
        emptyType="individual"
      />

      {/* Collection settings */}
      {(settings.team_waiver_required || settings.individual_waiver_required) && (
        <div className="border border-border rounded-lg p-4 space-y-3 bg-card">
          <h4 className="text-sm font-semibold">Collection settings</h4>
          <div>
            <label className="text-xs font-medium text-muted-foreground">When to collect</label>
            <RadioGroup
              value={settings.collect_at}
              onValueChange={(v) => persist({ ...settings, collect_at: v as any })}
              className="mt-1 grid grid-cols-1 gap-1.5"
            >
              {[
                { v: "registration", t: "During registration (default)" },
                { v: "before_event", t: "Email before event" },
                { v: "on_arrival", t: "On arrival at check-in" },
              ].map(o => (
                <label key={o.v} className="flex items-center gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value={o.v} /> {o.t}
                </label>
              ))}
            </RadioGroup>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={settings.block_unsigned}
              onCheckedChange={(v) => persist({ ...settings, block_unsigned: !!v })}
            />
            Block scheduling/check-in until waivers are signed
          </label>
        </div>
      )}

      {previewing && (
        <Dialog open onOpenChange={() => setPreviewing(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{previewing.title}</DialogTitle></DialogHeader>
            {previewing.source_type === "text" && <RichTextDisplay html={previewing.body_html ?? ""} />}
            {previewing.source_type === "external_url" && (
              <a href={previewing.external_url!} target="_blank" rel="noopener noreferrer" className="text-primary inline-flex items-center gap-1">
                {previewing.external_url} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            {previewing.source_type === "pdf_upload" && previewing.pdf_filename && (
              <p className="text-sm text-muted-foreground">PDF: {previewing.pdf_filename}</p>
            )}
            <DialogFooter><Button variant="outline" onClick={() => setPreviewing(null)}>Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function WaiverSlot({
  title, toggleLabel, required, onToggleRequired,
  selectedId, options, onSelect, onPreview, emptyType,
}: any) {
  const selected: WaiverCatalogItem | undefined = options.find((o: WaiverCatalogItem) => o.id === selectedId);
  return (
    <div className="border border-border rounded-lg p-4 space-y-3 bg-card">
      <h3 className="text-sm font-semibold flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-primary" /> {title}
      </h3>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={required} onCheckedChange={(v) => onToggleRequired(!!v)} />
        {toggleLabel}
      </label>

      {required && (
        options.length === 0 ? (
          <div className="border border-dashed border-border rounded p-4 text-sm text-muted-foreground">
            No {emptyType} waivers available.{" "}
            <a href="/admin-dashboard/waivers" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Create one in the catalog →
            </a>
          </div>
        ) : (
          <>
            <select
              value={selectedId ?? ""}
              onChange={(e) => onSelect(e.target.value || null)}
              className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
            >
              <option value="">— Select {emptyType} waiver —</option>
              {options.map((o: WaiverCatalogItem) => (
                <option key={o.id} value={o.id}>{o.title} (v{o.version})</option>
              ))}
            </select>

            {selected && (
              <div className="border border-border rounded-md p-3 bg-background space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-medium text-sm">{selected.title}</div>
                    <div className="flex flex-wrap gap-1.5 mt-1 text-xs">
                      <SourceChip source={selected.source_type} />
                      <span className="px-1.5 py-0.5 rounded bg-secondary border border-border">v{selected.version}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => onPreview(selected)}>Preview</Button>
                  <a
                    href="/admin-dashboard/waivers"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline self-center inline-flex items-center gap-1"
                  >
                    Edit in catalog <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            )}
          </>
        )
      )}
    </div>
  );
}

function SourceChip({ source }: { source: string }) {
  return (
    <span className="px-1.5 py-0.5 rounded bg-secondary border border-border inline-flex items-center gap-1">
      {source === "pdf_upload" && <><FileText className="w-3 h-3" /> PDF</>}
      {source === "text" && <><Pencil className="w-3 h-3" /> Text</>}
      {source === "external_url" && <><LinkIcon className="w-3 h-3" /> External</>}
    </span>
  );
}
